import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, TextField, List, ListItem, ListItemText, IconButton, Paper, Divider, Badge, Chip, CircularProgress, Alert, Avatar } from '@mui/material';
import { 
  ThumbUp, 
  Delete, 
  ThumbUpAlt, 
  Chat,
  Add,
  Send,
  EmojiEvents,
  CheckCircle
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';

interface Solution {
  id: string;
  text: string;
  votes: number;
  voters: string[];
  userId: string;
  userName: string;
}

const mockUserId = 'user1'; // Replace with real user ID from auth later

const DecisionPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // Solutions
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [newSolution, setNewSolution] = useState('');
  const [solutionLoading, setSolutionLoading] = useState(false);
  const [solutionError, setSolutionError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<any[]>([]); // Changed to any[] to accommodate userId and userName
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Solutions
    const unsubSolutions = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_solutions`), snap => {
      setSolutions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Solution)));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubSolutions();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Add Solution
  const handleAddSolution = async () => {
    if (newSolution.trim() && teamId && sprintId) {
      setSolutionLoading(true);
      setSolutionError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_solutions`), {
          text: newSolution.trim(),
          votes: 0,
          voters: [],
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewSolution('');
      } catch (err) {
        setSolutionError('Failed to add solution.');
      }
      setSolutionLoading(false);
    }
  };

  // Vote Solution
  const handleVote = async (id: string) => {
    if (!teamId || !sprintId) return;
    setSolutionLoading(true);
    setSolutionError(null);
    try {
      const solRef = doc(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_solutions`, id);
      const solSnap = await getDoc(solRef);
      if (solSnap.exists()) {
        const data = solSnap.data();
        if (!data.voters.includes(mockUserId)) {
          await updateDoc(solRef, {
            votes: (data.votes || 0) + 1,
            voters: [...(data.voters || []), mockUserId]
          });
        }
      }
    } catch (err) {
      setSolutionError('Failed to vote.');
    }
    setSolutionLoading(false);
  };

  // Delete Solution
  const handleDeleteSolution = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_solutions`, id));
    } catch (err) {
      setSolutionError('Failed to delete solution.');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/decision_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  // Find top-voted solution(s)
  const maxVotes = solutions.length ? Math.max(...solutions.map(s => s.votes)) : 0;
  const topSolutionIds = solutions.filter(s => s.votes === maxVotes && maxVotes > 0).map(s => s.id);

  return (
    <div className="space-y-6">
      {/* Solution Submission Section */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
            <ThumbUpAlt className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Submit Solutions</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Propose and vote on final solutions</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Solution Summary"
            value={newSolution}
            onChange={e => setNewSolution(e.target.value)}
            size="small"
            fullWidth
            disabled={solutionLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Describe your proposed solution..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddSolution()}
          />
          <Button
            variant="contained"
            onClick={handleAddSolution}
            disabled={solutionLoading || !newSolution.trim()}
            startIcon={<Add />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Submit
          </Button>
        </div>

        {solutionLoading && (
          <div className="flex items-center gap-2 text-emerald-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Submitting solution...</span>
          </div>
        )}
        
        {solutionError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {solutionError}
          </Alert>
        )}

        <div className="space-y-3">
          {solutions.map(sol => (
            <div key={sol.id} className={`bg-white rounded-xl p-4 border transition-all duration-200 ${
              topSolutionIds.includes(sol.id) 
                ? 'border-emerald-300 shadow-lg bg-gradient-to-r from-emerald-50 to-green-50' 
                : 'border-gray-200 hover:shadow-md'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-gray-900 font-['Inter'] font-medium">{sol.text}</p>
                    {topSolutionIds.includes(sol.id) && (
                      <Chip 
                        icon={<EmojiEvents />}
                        label="Top Solution" 
                        color="success" 
                        size="small"
                        sx={{ 
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        }}
                      />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 font-['Inter']">by {sol.userName || 'Unknown'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={sol.voters.includes(mockUserId) ? "contained" : "outlined"}
                    size="small"
                    startIcon={<ThumbUp />}
                    onClick={() => handleVote(sol.id)}
                    disabled={sol.voters.includes(mockUserId) || solutionLoading}
                    sx={{
                      borderRadius: '8px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 2,
                      color: sol.voters.includes(mockUserId) ? '#ffffff' : '#10b981',
                      borderColor: '#10b981',
                      ...(sol.voters.includes(mockUserId) && {
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          color: '#ffffff'
                        }
                      })
                    }}
                  >
                    {sol.votes}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSolution(sol.id)}
                    disabled={solutionLoading}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: '#fef2f2' }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>

        {solutions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <ThumbUpAlt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No solutions submitted yet. Be the first to propose a solution!</p>
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionPhase; 